<script setup lang="ts" generic="T extends Record<string, any>">
import { computed, ref } from 'vue';

export type ColumnDef<T> = {
  key: keyof T;
  label: string;
  sortable?: boolean;
  render?: (row: T) => string;
  compareValue?: (row: T) => string | number;
  sortFn?: (a: T, b: T) => number;
  isBadge?: boolean;
  badgeClass?: (row: T) => string;
};

type SortOrder = 'none' | 'asc' | 'desc';

const sortKey = ref<keyof T | null>(null);
const sortOrder = ref<SortOrder>('none');

const props = withDefaults(
  defineProps<{
    isShowHeader?: boolean;
    isSelectable?: boolean;
    columns: ColumnDef<T>[];
    rows: T[];
    isAllSelected?: boolean;
    isSomeSelected?: boolean;
    isProcessingDone?: boolean;
    failedIds?: Map<string, string | undefined>;
    selectedIds?: Set<string>;
    rowKey?: (row: T) => string;
    rowClass?: (row: T) => string[];
  }>(),
  {
    isShowHeader: true,
    isSelectable: true,
    isAllSelected: false,
    isSomeSelected: false,
    isProcessingDone: true,
    failedIds: () => new Map(),
    selectedIds: () => new Set(),
    rowKey: () => 'rowKey',
    rowClass: () => [],
  },
);

defineEmits<{
  (e: 'navigateToItem', id: T): void;
  (e: 'toggleSelection', id: string): void;
  (e: 'toggleAll'): void;
}>();

const getSortIndicator = (order: SortOrder) => {
  if (order === 'asc') return '▲';
  if (order === 'desc') return '▼';
  return '';
};

const toggleSortOrder = (currentOrder: SortOrder): SortOrder => {
  if (currentOrder === 'asc') return 'desc';
  if (currentOrder === 'desc') return 'none';
  return 'asc';
};

const toggleSortState = (key: keyof T): void => {
  if (sortKey.value === key) {
    sortOrder.value = toggleSortOrder(sortOrder.value);
  } else {
    sortKey.value = key;
    sortOrder.value = 'asc';
  }

  if (sortOrder.value === 'none') {
    sortKey.value = null;
  }
};

const sortedRows = computed(() => {
  if (!sortKey.value || sortOrder.value === 'none') return props.rows;

  return [...props.rows].sort((a, b) => {
    const aVal = a[sortKey.value!] as string | number;
    const bVal = b[sortKey.value!] as string | number;

    const col = props.columns.find((column) => column.key === sortKey.value);

    if (col?.sortFn) {
      const result = col.sortFn(a, b);
      return sortOrder.value === 'desc' ? result * -1 : result;
    }

    const aCmp = col?.compareValue ? col.compareValue(a) : aVal;
    const bCmp = col?.compareValue ? col.compareValue(b) : bVal;

    if (aCmp === bCmp) return 0;
    const result = aCmp > bCmp ? 1 : -1;

    return sortOrder.value === 'desc' ? result * -1 : result;
  });
});
</script>

<template>
  <div class="invoice-table">
    <table>
      <thead v-if="isShowHeader">
        <tr>
          <th v-if="isSelectable" class="col-checkbox">
            <input
              type="checkbox"
              name="alla"
              :disabled="rows.length === 0"
              :checked="isAllSelected"
              :indeterminate="isSomeSelected && !isAllSelected"
              @change="$emit('toggleAll')"
            />
          </th>
          <th
            v-for="column in columns"
            :key="String(column.key)"
            @click="toggleSortState(column.key)"
            :class="{ active: sortKey === column.key }"
          >
            {{ column.label }}
            <span v-if="sortKey === column.key" class="sort-indicator">
              {{ getSortIndicator(sortOrder) }}
            </span>
          </th>
          <th v-if="$slots.subColumn">
            <slot name="subColumn" />
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="item in sortedRows" :key="rowKey(item)">
          <tr :class="rowClass(item)">
            <td v-if="isSelectable">
              <input
                type="checkbox"
                :checked="
                  isProcessingDone
                    ? failedIds.has(rowKey(item))
                    : selectedIds.has(rowKey(item))
                "
                @change="$emit('toggleSelection', rowKey(item))"
              />
            </td>
            <td
              v-for="column in columns"
              :key="String(column.key)"
              @click="$emit('navigateToItem', item)"
            >
              <span
                v-if="column.isBadge"
                class="status-badge"
                :class="column.badgeClass ? column.badgeClass(item) : ''"
              >
                {{ column.render ? column.render(item) : item[column.key] }}
              </span>
              <template v-else>
                {{ column.render ? column.render(item) : item[column.key] }}
              </template>
            </td>
            <td v-if="$slots.subColumn" :item="item" class="col-action">
              <slot name="subColumn" :item="item" />
            </td>
          </tr>
          <tr v-if="$slots.subRow" class="row-message">
            <td
              :colspan="
                columns.length +
                (isSelectable ? 1 : 0) +
                ($slots.subColumn ? 1 : 0)
              "
            >
              <slot name="subRow" :item="item" />
            </td></tr
        ></template>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
@use '@/assets/style/variables';
@use 'bulma/sass/utilities/initial-variables' as *;
@use '@/assets/style/popup';

.col-action {
  text-align: right;
  vertical-align: middle;
  white-space: nowrap;
  width: 1%; // shrinks to fit content
}

.status-badge {
  display: inline-block;
  padding: 2px 12px;
  font-size: 0.82em;
  font-weight: 600;
  border-radius: 9999px;
  white-space: nowrap;

  &.incoming {
    background-color: #90caf9;
  }
  &.undercertificate {
    background-color: var(--invoice-undercertificate);
  }
  &.parked {
    background-color: var(--invoice-parked);
  }
  &.disputed {
    background-color: var(--invoice-disputed);
  }
  &.voided {
    background-color: var(--invoice-voided);
  }
  &.approved {
    background-color: var(--invoice-approved);
  }
  &.verified-debt {
    background-color: var(--invoice-verified-debt);
  }
  &.paid {
    background-color: var(--invoice-paid);
  }
}

// .table-container {
//   position: relative;
//
//   &.is-processing {
//     .invoice-table {
//       opacity: 0.4;
//       pointer-events: none;
//       user-select: none;
//       filter: grayscale(40%);
//       transition:
//         opacity 0.25s ease,
//         filter 0.25s ease;
//     }
//   }
// }

.invoice-table {
  margin-bottom: 1rem;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow-x: auto;

  table {
    width: 100%;
    min-width: 800px;
    border-collapse: collapse;

    th,
    td {
      padding: 7px;
      text-align: left;
      border: none;
      border-bottom: 1px solid #eee;
      font-size: 0.9em;
    }

    thead {
      background-color: white;

      th {
        font-weight: 600;
        box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.05);
        border-bottom: 2px solid #ddd;
        position: relative;
        user-select: none;

        &.sortable {
          cursor: pointer;

          &:hover {
            background-color: rgba(0, 0, 0, 0.03);
          }
        }

        .sort-indicator {
          position: absolute;
          right: 5px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 0.8em;
          color: #555;
        }
      }

      th:first-child {
        border-top-left-radius: 8px;
      }

      th:last-child {
        border-top-right-radius: 8px;
      }
    }

    tbody {
      tr {
        transition: background-color 0.15s ease;

        &.selected {
          background-color: var(--table-grey) !important;
        }

        td:last-child {
          justify-self: flex-end;
        }

        &.failed {
          background-color: color-mix(
            in srgb,
            var(--light-grey) 75%,
            var(--table-red)
          ) !important;

          &:hover {
            background-color: color-mix(
              in srgb,
              var(--light-grey) 25%,
              var(--table-red)
            ) !important;
          }

          td {
            background-color: var(--table-orage);
            color: var(--text-danger);
          }
        }

        &:hover {
          background-color: var(--light-grey) !important;
          cursor: pointer;
        }

        &:nth-child(even) {
          background-color: #f5f5f5;
        }

        &.row-message {
          background-color: color-mix(
            in srgb,
            var(--light-grey) 75%,
            var(--table-red)
          ) !important;
          cursor: default;
        }

        &.row-message:hover {
          background-color: color-mix(
            in srgb,
            var(--light-grey) 75%,
            var(--table-red)
          ) !important;
          cursor: default;
        }

        &:last-child td {
          border-bottom: none;
        }
      }

      tr.approved:hover td,
      tr.verified-debt:hover td,
      tr.verified-payed:hover td {
        box-shadow: inset 0 0 0 1000px rgba(0, 0, 0, 0.04);
      }

      tr.even {
        background-color: var(--light-grey);
      }

      tr.odd {
        background-color: var(--table-row);
      }

      tr:last-child td:first-child {
        border-bottom-left-radius: 8px;
      }

      tr:last-child td:last-child {
        border-bottom-right-radius: 8px;
      }
    }
  }
}

.col-checkbox {
  cursor: default;

  input[type='checkbox'] {
    cursor: pointer;
  }
}
</style>
